import { Loader2 } from "lucide-react"
import * as React from "react"
import { Link, useNavigate, useSearchParams } from "react-router"

import { PasswordInput } from "@/components/domain/password-input"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"

import { AuthLayout } from "./auth-layout"
import { type FieldErrors, hasFieldError, type LoginError, validateLoginInput } from "./auth-data"
import { useCurrentUser, useLoginMutation } from "./auth-store"

const NO_ERRORS: FieldErrors = { email: null, password: null }

/**
 * `?redirect=` is user-controlled — anything other than a same-origin
 * path lets an attacker turn our post-login navigate into an open
 * redirect (phishing). Rejected forms: schemes (`https:`), authority
 * shortcuts (`//host`, `/\\host`), and any control characters that
 * could smuggle through downstream routers.
 */
function safeRedirect(target: string | null): string {
  if (!target) return "/reviews"
  if (target.length > 512) return "/reviews"
  if (/[\r\n\t\0]/.test(target)) return "/reviews"
  if (!target.startsWith("/")) return "/reviews"
  if (target.startsWith("//") || target.startsWith("/\\")) return "/reviews"
  if (/^\/+[a-z][a-z0-9+.-]*:/i.test(target)) return "/reviews"
  return target
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = safeRedirect(searchParams.get("redirect"))

  const login = useLoginMutation()
  const currentUser = useCurrentUser()
  const currentUserId = currentUser?.id ?? null

  // A reviewer who already has a session should not see the form.
  React.useEffect(() => {
    if (currentUserId) navigate(redirectTo, { replace: true })
  }, [currentUserId, navigate, redirectTo])

  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [rememberMe, setRememberMe] = React.useState(false)
  const rememberId = React.useId()
  const [touched, setTouched] = React.useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false,
  })
  const [attempted, setAttempted] = React.useState(false)

  const fieldErrors = React.useMemo<FieldErrors>(() => {
    if (!attempted && !touched.email && !touched.password) return NO_ERRORS
    const errors = validateLoginInput({ email, password })
    return {
      email: attempted || touched.email ? errors.email : null,
      password: attempted || touched.password ? errors.password : null,
    }
  }, [email, password, touched, attempted])

  const topError = useTopLevelError(login.error)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setAttempted(true)
    const errors = validateLoginInput({ email, password })
    if (hasFieldError(errors)) return
    if (login.isPending) return
    try {
      await login.mutateAsync({ email: email.trim(), password, rememberMe })
      navigate(redirectTo, { replace: true })
    } catch {
      /* surfaced via login.error */
    }
  }

  return (
    <AuthLayout
      eyebrow="Sign-in"
      title="Sign in"
      subtitle="Review printed labels against their approved masters, and share the findings with your client."
      footer={
        <p>
          No account yet?{" "}
          <span className="text-text-2">
            Your Pyxis contact can set one up — access is granted per engagement.
          </span>
        </p>
      }
    >
      <form
        onSubmit={onSubmit}
        className="space-y-4"
        noValidate
        aria-busy={login.isPending || undefined}
      >
        <div className="sr-only" aria-live="polite" role="status">
          {login.isPending ? "Signing in…" : topError ? topError.body : ""}
        </div>

        {topError && (
          <Alert tone="error" size="sm" title={topError.title}>
            {topError.body}
          </Alert>
        )}

        <FormField label="Email" required error={fieldErrors.email}>
          <Input
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@pyxis.com"
            inputSize="lg"
            value={email}
            onChange={(e) => {
              setEmail(e.currentTarget.value)
              if (login.error) login.reset()
            }}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            disabled={login.isPending}
            autoFocus
          />
        </FormField>

        <FormField label="Password" required error={fieldErrors.password}>
          <PasswordInput
            autoComplete="current-password"
            inputSize="lg"
            value={password}
            onChange={(e) => {
              setPassword(e.currentTarget.value)
              if (login.error) login.reset()
            }}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            disabled={login.isPending}
          />
        </FormField>

        <div className="flex items-center justify-between gap-3">
          {/* The shared Checkbox, not a bare <input>: the native control ignores the border
              classes and renders unchecked as a solid dark square. */}
          <div className="inline-flex items-center gap-2 text-body-sm text-text-2">
            <Checkbox
              id={rememberId}
              checked={rememberMe}
              onCheckedChange={(v) => {
                setRememberMe(v === true)
              }}
              disabled={login.isPending}
            />
            <label htmlFor={rememberId} className="cursor-pointer select-none">
              Remember me
            </label>
          </div>
          <Link
            to="/forgot-password"
            className="text-body-sm text-brand hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          leadingIcon={login.isPending ? <Loader2 className="animate-spin" /> : undefined}
          disabled={login.isPending}
        >
          {login.isPending ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthLayout>
  )
}

interface TopError {
  title: string
  body: string
}

/**
 * Turn a LoginError into a banner. Countdown ticks per second while a
 * rate-limit error carries a `retryAfterSeconds`. Returns null when
 * there is no error to show.
 */
function useTopLevelError(error: LoginError | null): TopError | null {
  const [remaining, setRemaining] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!error || error.retryAfterSeconds == null) {
      setRemaining(null)
      return
    }
    setRemaining(error.retryAfterSeconds)
    const t = setInterval(() => {
      setRemaining((prev) => {
        if (prev == null) return null
        if (prev <= 1) {
          clearInterval(t)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(t)
  }, [error])

  if (!error) return null
  switch (error.kind) {
    case "invalid_credentials":
      return { title: "Sign in failed", body: "Email or password is incorrect." }
    case "account_locked":
      return {
        title: "Account locked",
        body: "This account is locked. Contact your Pyxis admin to unlock it.",
      }
    case "rate_limited":
      return {
        title: "Too many attempts",
        body: remaining && remaining > 0 ? `Try again in ${remaining}s.` : "You can try again now.",
      }
    case "network":
      return {
        title: "Network problem",
        body: "We couldn't reach the server. Check your connection and try again.",
      }
    case "server":
      return {
        title: "Server error",
        body: "Something went wrong on our side. Please try again in a moment.",
      }
    default:
      return { title: "Sign in failed", body: error.message }
  }
}
